/**
 * MobileLayout — wrapper for mobile field worker pages
 * Sticky top bar (project switcher + More button) via the TopBar atom,
 * scrollable content area, and MobileNav bottom bar.
 *
 * Changes (M1 polish):
 * - minHeight: 100dvh (not 100vh) — correct on iOS with notch/home bar
 * - overflowX: hidden — prevents horizontal scroll caused by wide children
 * - Scroll container has height + overflow-y: auto so content scrolls properly
 * - paddingBottom accounts for keyboard via env(keyboard-inset-height)
 * - More drawer lifted here and triggered from top-bar button (not bottom tab)
 *
 * Phase A: top-bar markup extracted to the `TopBar` atom (spec §06.5).
 *
 * MOBILE-CHROME-V2-REAL-RESTYLE:
 * - useSafeAreaInsets for bottom padding (env(safe-area-inset-bottom))
 * - Pull-to-refresh via visualViewport scroll position + touch delta
 * - Viewport height tracking via window.visualViewport (keyboard-aware)
 *
 * FIX (mobile-header-reduce-dead-space #51):
 * - Removed paddingTop: env(safe-area-inset-top) from the outer container.
 *   TopBar already applies calc(10px + env(safe-area-inset-top)) to its own
 *   padding, so the container-level safe-area was doubling the inset and
 *   creating ~60px of dead space above the project selector on Dynamic Island
 *   devices. Letting TopBar own it also means --bg-surface extends into the
 *   status bar zone (correct iOS appearance).
 */
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { MoreHorizontal } from 'lucide-react'
import { MobileNav } from '@/components/organisms/MobileNav'
import { MobileProjectSwitcher } from '@/components/molecules/MobileProjectSwitcher'
import { MobileMoreDrawer } from '@/components/organisms/MobileMoreDrawer'
import { HelpPanel } from '@/components/organisms/HelpPanel'
import { TopBar } from '@/components/atoms/TopBar'
import { IconButton } from '@/components/atoms/IconButton'
import { useSafeAreaInsets } from '@/hooks/useSafeAreaInsets'
import { usePermissionsBootstrap } from '@/lib/usePermissions'

// Pages where the project switcher + top bar don't apply
const HIDE_SWITCHER_PATHS = ['/m/messages', '/m/settings', '/m/settings/team', '/m/settings/roles', '/m/settings/company']

// Pull-to-refresh thresholds
const PTR_TRIGGER_PX = 72   // drag this far to trigger refresh
const PTR_MAX_PX     = 96   // clamp visual pull indicator here

type PtrState = 'idle' | 'pulling' | 'releasing' | 'refreshing'

interface MobileLayoutProps {
  children: React.ReactNode
  userName?: string
  hideSwitcher?: boolean
  /** Called when user completes a pull-to-refresh gesture. Must resolve to end the spinner. */
  onRefresh?: () => Promise<void>
}

export function MobileLayout({ children, hideSwitcher, onRefresh }: MobileLayoutProps) {
  usePermissionsBootstrap()
  const location = useLocation()
  const shouldHide = hideSwitcher || HIDE_SWITCHER_PATHS.some(p => location.pathname.startsWith(p))
  const [moreOpen, setMoreOpen] = useState(false)
  const [helpPanelOpen, setHelpPanelOpen] = useState(false)
  const insets = useSafeAreaInsets()

  // visualViewport height — keyboard-aware (iOS shrinks this when keyboard opens)
  const [viewportH, setViewportH] = useState(
    () => typeof window !== 'undefined' ? (window.visualViewport?.height ?? window.innerHeight) : 812
  )
  useEffect(() => {
    const vv = window.visualViewport
    if (!vv) return
    const update = () => setViewportH(vv.height)
    vv.addEventListener('resize', update)
    vv.addEventListener('scroll', update)
    return () => {
      vv.removeEventListener('resize', update)
      vv.removeEventListener('scroll', update)
    }
  }, [])

  // Pull-to-refresh state
  const [ptrState, setPtrState]   = useState<PtrState>('idle')
  const [pullY, setPullY]         = useState(0)
  const scrollRef                 = useRef<HTMLDivElement>(null)
  const touchStartYRef            = useRef(0)
  const isPullingRef              = useRef(false)

  const onTouchStart = useCallback((e: React.TouchEvent) => {
    touchStartYRef.current = e.touches[0].clientY
    isPullingRef.current = false
  }, [])

  const onTouchMove = useCallback((e: React.TouchEvent) => {
    const el = scrollRef.current
    if (!el || el.scrollTop > 0) return
    if (ptrState === 'refreshing') return
    const dy = e.touches[0].clientY - touchStartYRef.current
    if (dy <= 0) return
    isPullingRef.current = true
    const clamped = Math.min(dy * 0.5, PTR_MAX_PX)
    setPullY(clamped)
    setPtrState(clamped >= PTR_TRIGGER_PX * 0.5 ? 'pulling' : 'idle')
  }, [ptrState])

  const onTouchEnd = useCallback(async () => {
    if (!isPullingRef.current) return
    isPullingRef.current = false
    if (pullY >= PTR_TRIGGER_PX * 0.5 && onRefresh) {
      setPtrState('refreshing')
      setPullY(40)
      try { await onRefresh() } finally {
        setPtrState('idle')
        setPullY(0)
      }
    } else {
      setPtrState('idle')
      setPullY(0)
    }
  }, [pullY, onRefresh])

  const ptrVisible = ptrState === 'pulling' || ptrState === 'refreshing'

  return (
    <div style={{
      height: viewportH,
      background: 'var(--bg-page)',
      display: 'flex',
      flexDirection: 'column',
      overflowX: 'hidden',
      // NOTE: safe-area-inset-top is intentionally NOT applied here.
      // TopBar owns it via paddingTop: calc(10px + env(safe-area-inset-top)).
      // Applying it here would double the inset and create a dead band above
      // the project selector on Dynamic Island / notch devices (#51).
    }}>
      {/* TopBar always renders for P-03 (⋯ is universal); project switcher hidden on settings/messages pages */}
      <TopBar
        left={!shouldHide ? <MobileProjectSwitcher /> : undefined}
        right={
          <IconButton aria-label="More" onClick={() => setMoreOpen(true)}>
            <MoreHorizontal size={20} />
          </IconButton>
        }
      />

      {/* Pull-to-refresh indicator */}
      {ptrVisible && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 8,
          height: pullY,
          overflow: 'hidden',
          transition: ptrState === 'refreshing' ? 'none' : 'height 80ms linear',
          color: 'var(--text-muted)',
          fontSize: 11.5,
          letterSpacing: 0.4,
          textTransform: 'uppercase',
          fontFamily: '"Karla", sans-serif',
          flexShrink: 0,
        }}>
          {ptrState === 'refreshing' ? (
            // Inline spinner — same pattern as Button atom
            <svg width={14} height={14} viewBox="0 0 16 16" style={{ animation: 'spin 0.7s linear infinite' }}>
              <circle cx={8} cy={8} r={6} fill="none" stroke="currentColor" strokeWidth={2} strokeDasharray="28 8"/>
              <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
            </svg>
          ) : (
            <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round">
              <polyline points="23 4 23 10 17 10"/><path d="M20.49 15a9 9 0 11-2.12-9.36L23 10"/>
            </svg>
          )}
          {ptrState === 'refreshing' ? 'Refreshing…' : 'Release to refresh'}
        </div>
      )}

      {/* Scrollable content area — touch handlers for pull-to-refresh */}
      <div
        ref={scrollRef}
        onTouchStart={onRefresh ? onTouchStart : undefined}
        onTouchMove={onRefresh ? onTouchMove : undefined}
        onTouchEnd={onRefresh ? onTouchEnd : undefined}
        style={{
          flex: 1,
          overflowY: 'auto',
          height: 0,  /* combined with flex:1 to make it scrollable */
          paddingBottom: `calc(78px + ${insets.bottom}px + env(keyboard-inset-height, 0px))`,
          WebkitOverflowScrolling: 'touch',
          overscrollBehavior: 'contain',
        } as React.CSSProperties}
      >
        {children}
      </div>

      {/* More drawer — triggered from top bar button */}
      <MobileMoreDrawer
        open={moreOpen}
        onClose={() => setMoreOpen(false)}
        onHelpClick={() => setHelpPanelOpen(true)}
      />

      {/* Help panel — triggered from More drawer */}
      <HelpPanel open={helpPanelOpen} onClose={() => setHelpPanelOpen(false)} />

      {/* Fixed bottom nav — rendered above safe-area-inset-bottom */}
      <MobileNav />
    </div>
  )
}
